const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const USE_MOCK = !BASE_URL || process.env.REACT_APP_USE_MOCK === 'true';
const MOCK_LATENCY = Number(process.env.REACT_APP_MOCK_LATENCY || 0);

// 逐領域覆寫：某個領域後端做好了，就用 REACT_APP_USE_MOCK_<領域>=false 單獨開真後端，
// 其他沒做的繼續走全域 mock。CRA 只會替換「字面」的 process.env.REACT_APP_*，
// 不支援動態組 key，所以這裡每個領域都要明確列出來。
const DOMAIN_MOCK_OVERRIDE = {
  products: process.env.REACT_APP_USE_MOCK_PRODUCTS,
  themes: process.env.REACT_APP_USE_MOCK_THEMES,
  roles: process.env.REACT_APP_USE_MOCK_ROLES,
  inquiries: process.env.REACT_APP_USE_MOCK_INQUIRIES,
  virtual: process.env.REACT_APP_USE_MOCK_VIRTUAL,
  orders: process.env.REACT_APP_USE_MOCK_ORDERS,
  dashboard: process.env.REACT_APP_USE_MOCK_DASHBOARD,
  savedcards: process.env.REACT_APP_USE_MOCK_SAVEDCARDS,
  account: process.env.REACT_APP_USE_MOCK_ACCOUNT,
};

// AuthContext 啟動後注入，讓 request() 可以觸發 refresh 或強制登出，以及取得當前 token
let _onRefresh = null;
let _onForceLogout = null;
let _getToken = null;

export function setAuthCallbacks(onRefresh, onForceLogout, getToken) {
  _onRefresh = onRefresh;
  _onForceLogout = onForceLogout;
  _getToken = getToken;
}

// 防止同時多個 401 各自觸發 refresh（Refresh Token Rotation 下只能用一次）
let _refreshPromise = null;

function getStoredToken() {
  // token 活在 React state，由 AuthContext 透過 _getToken 注入
  return _getToken?.() ?? null;
}

export function mockResponse(data) {
  return new Promise((resolve) => {
    const value = typeof data === 'function' ? data() : data;
    if (MOCK_LATENCY > 0) setTimeout(() => resolve(value), MOCK_LATENCY);
    else resolve(value);
  });
}

async function doFetch(path, options = {}, token) {
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  return fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',  // 讓瀏覽器自動帶 httpOnly cookie（refresh_token）
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
}

// 這些 endpoint 401 本身就是「帳密錯誤 / refresh 失效」的業務錯誤，
// 不應該再觸發自動 refresh + 強制登出，否則會把錯誤訊息洗成 reload。
const AUTH_PATHS = ['/api/auth/login', '/api/auth/register', '/api/auth/refresh'];

export async function request(path, options = {}) {
  const token = getStoredToken();

  let res = await doFetch(path, options, token);

  // 何時要自動 refresh 後重試一次（auth endpoints 除外）：
  //   - 401：access token 過期或無效。
  //   - 403 且「當前沒有 token」：典型是「重整後 silent refresh 還沒補回 token，
  //     頁面就先發了需權限的 API」這種 race condition。先 refresh 再重打即可救回。
  //     （已有 token 卻 403 = 真的沒權限，不該 refresh，直接讓它擋下。）
  const isAuthEndpoint = AUTH_PATHS.some(p => path.startsWith(p));
  const tokenMissing = !token;
  const shouldRetry = _onRefresh && !isAuthEndpoint &&
    (res.status === 401 || (res.status === 403 && tokenMissing));

  if (shouldRetry) {
    try {
      if (!_refreshPromise) {
        _refreshPromise = _onRefresh().finally(() => { _refreshPromise = null; });
      }
      const newToken = await _refreshPromise;
      res = await doFetch(path, options, newToken);
    } catch {
      // refresh 失敗 → 強制登出
      _onForceLogout?.();
      const err = new Error('登入已過期，請重新登入');
      err.status = 401;
      throw err;
    }
  }

  if (!res.ok) {
    let message = `請求失敗 (${res.status})`;
    let code = null;
    try {
      const data = await res.json();
      message = data.message || message;
      code = data.code ?? null;
    } catch {}
    const err = new Error(message);
    err.status = res.status;
    err.code = code;
    throw err;
  }

  // 204 No Content（如 DELETE）沒有 body，直接呼叫 res.json() 會丟解析錯誤
  if (res.status === 204) return null;
  return res.json();
}

// 傳入領域名稱（如 'products'）可讀該領域的覆寫設定；不傳則用全域設定。
export const isMockMode = (domain) => {
  const override = domain ? DOMAIN_MOCK_OVERRIDE[domain] : undefined;
  if (override === 'true') return true;
  if (override === 'false') return false;
  return USE_MOCK;
};
